'use client'

import { menuData, simpleLinks } from '@/lib/menu-data'
import { safeJsonLd } from '@/lib/json-ld'

/**
 * SiteNavigationElement JSON-LD for the primary site navigation (chrome
 * SEO/LLM optimization, owner correction 07/20/2026).
 *
 * Rendered ONCE from the root layout. Derives every entry
 * from lib/menu-data.ts — the SAME data the visible header renders — so the
 * schema can never drift from the real nav. Two tiers are emitted:
 *   1. the top-level nav categories (their landing hubs), and
 *   2. every mega-menu item link (the money collections/hubs/data assets),
 * each as a SiteNavigationElement with the money-keyword anchor as its name.
 *
 * URLs are absolute against the production origin. No internal/admin route
 * can appear here: the source list is the curated public menu only.
 *
 * WHY THIS IS A CLIENT COMPONENT (FRIDGE-929, measured 09/17/2026)
 * ----------------------------------------------------------------
 * The schema is still server-rendered: a client component is SSR'd into the
 * HTML exactly like a server component, so every crawler and every non-JS
 * client receives the same <script type="application/ld+json"> at the same
 * position (tests/chrome-schema-rsc-dedupe.test.ts renders it with
 * react-dom/server and pins that). What changes is the React Server
 * Components flight payload: a SERVER component's rendered output is
 * serialised into the `self.__next_f.push` stream so the client can hydrate
 * it, which put a second, JSON-escaped copy of this 12,323-byte schema into
 * EVERY page — 13,821 bytes per document on production, measured on the
 * product, brand, filters, parts, press, local and home pages alike. A client
 * component ships only a module reference in that stream, and the menu data
 * it reads is already in every page's JavaScript through
 * components/site-header.tsx, so the bundle grows by the component alone.
 */
const ORIGIN = 'https://fridge.com'

interface NavEntry {
  name: string
  url: string
}

function collectNavEntries(): NavEntry[] {
  const seen = new Set<string>()
  const entries: NavEntry[] = []
  const push = (name: string, href: string) => {
    if (!href || !href.startsWith('/')) return
    const url = ORIGIN + href
    if (seen.has(url)) return
    seen.add(url)
    entries.push({ name, url })
  }
  for (const category of menuData) {
    push(category.trigger, category.href)
    for (const section of category.sections) {
      for (const item of section.items) {
        push(item.title, item.href)
      }
    }
  }
  for (const link of simpleLinks) {
    push(link.title, link.href)
  }
  return entries
}

export function SiteNavSchema() {
  const entries = collectNavEntries()
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Fridge.com primary navigation',
    itemListElement: entries.map((e, i) => ({
      '@type': 'SiteNavigationElement',
      position: i + 1,
      name: e.name,
      url: e.url,
    })),
  }
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  )
}
